import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/shared/context/TeamContext'
import { clientesApi } from '../api/clientes.api'
import type { ReporteClienteCreateInput, ReporteClienteUpdateInput } from '../types'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function useClientesNav() {
  const { selectedArea } = useTeam()
  const { data, isLoading } = useQuery({
    queryKey: ['reportes-clientes', selectedArea?.id, { page: 1, page_size: 500 }],
    queryFn: () => clientesApi.getReportes({ page_size: 500 }),
  })

  const grupos = useMemo(() => {
    if (!data?.results) return []
    const seen = new Map<number, string>()
    for (const r of data.results) {
      if (!seen.has(r.grupo_id)) seen.set(r.grupo_id, r.grupo_nombre)
    }
    return Array.from(seen, ([id, name]) => ({ id, name, slug: toSlug(name) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  return { grupos, isLoading }
}

export function useReportes(params?: {
  page?: number
  page_size?: number
  search?: string
  grupo_id?: number
  categoria?: string
  activo?: boolean
}) {
  const { selectedArea } = useTeam()
  return useQuery({
    queryKey: ['reportes-clientes', selectedArea?.id, params],
    queryFn: () => clientesApi.getReportes(params),
  })
}

export function useCreateReporte() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ReporteClienteCreateInput) =>
      clientesApi.createReporte(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-clientes'] })
    },
  })
}

export function useUpdateReporte() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ReporteClienteUpdateInput }) =>
      clientesApi.updateReporte(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-clientes'] })
    },
  })
}

export function useDeleteReporte() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => clientesApi.deleteReporte(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-clientes'] })
    },
  })
}

export function useToggleActivo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => clientesApi.toggleActivo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-clientes'] })
    },
  })
}

export function useCategorias() {
  return useQuery({
    queryKey: ['categorias-clientes'],
    queryFn: () => clientesApi.getCategorias(),
  })
}

export function useGruposDisponibles() {
  return useQuery({
    queryKey: ['grupos-disponibles-clientes'],
    queryFn: () => clientesApi.getGruposDisponibles(),
  })
}
